// Kendi kodumuz (beltasapp ızgarasında satır penceresi yok) — docs/ODEME-PLANLAMA-IZGARA-PLAN.md Faz 1.
import { defaultRangeExtractor, useVirtualizer, type Range } from '@tanstack/react-virtual'
import { useCallback, type RefObject } from 'react'

/** Bu sayının üstünde yalnız görünen satırlar çizilir; altında tüm satırlar (bugünkü davranış). */
export const VIRTUAL_MIN_ROWS = 100
/** Satır yüksekliği (px): hücreler `h-9`. Ölçülmez. */
export const GRID_ROW_HEIGHT = 36
/** Yapışkan başlık satırının yüksekliği (px). */
const HEAD_HEIGHT = 36
const OVERSCAN = 10
/** İlk çizimde kapsayıcı henüz ölçülmediğinde varsayılan görünür yükseklik (px). */
const INITIAL_HEIGHT = 600

interface UseGridWindowOptions {
  rowCount: number
  scrollRef: RefObject<HTMLElement | null>
  /** Pencere dışında kalsa da çizilecek satırlar (aktif hücre, açık editör): editör sökülmez. */
  pinned: Array<number | undefined>
}

/**
 * Izgaranın satır penceresi. `null` = sanallaştırma kapalı (az satır). Açıkken çizilecek satır
 * dizinleri artan sırada döner; aradaki boşluklar sabit satır yüksekliğinden hesaplanır.
 * Kütüphane nesnesi kendini değiştirdiği için dışarı yalnız düz değerler çıkar (`use-virtual-rows.ts`).
 */
export function useGridWindow({ rowCount, scrollRef, pinned }: UseGridWindowOptions) {
  const enabled = rowCount > VIRTUAL_MIN_ROWS
  const [p0, p1] = pinned
  const rangeExtractor = useCallback(
    (range: Range) => {
      const indexes = defaultRangeExtractor(range)
      const extra = [p0, p1].filter(
        (i): i is number => i !== undefined && i < range.count && !indexes.includes(i),
      )
      return extra.length ? [...indexes, ...extra].sort((a, b) => a - b) : indexes
    },
    [p0, p1],
  )
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: enabled ? rowCount : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => GRID_ROW_HEIGHT,
    overscan: OVERSCAN,
    scrollMargin: HEAD_HEIGHT,
    scrollPaddingStart: HEAD_HEIGHT,
    initialRect: { width: 0, height: INITIAL_HEIGHT },
    rangeExtractor,
  })
  const scrollToRow = useCallback(
    (row: number) => virtualizer.scrollToIndex(row, { align: 'auto' }),
    [virtualizer],
  )
  return {
    indexes: enabled ? virtualizer.getVirtualItems().map((item) => item.index) : null,
    scrollToRow,
  }
}
